import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { AppService } from '../app.service';
import { Company } from '../models/common/company.model';
import { DeleteShot } from '../models/common/delete-shot.model';
import { OrderDetailsSchema } from '../models/common/order-details-schema.model';
import { OrderListItem } from '../models/common/order-list-item.model';
import { OrdersListFilters } from '../models/common/orders-list-filters.model';
import { OrdersListSchema } from '../models/common/orders-list-schema.model';
import { AssignShotData } from '../models/photographer/assign-shot-data.model';
import { PhotographerOrderDetails } from '../models/photographer/photographer-order-details.model';
import { PhotographerRejectedShot } from '../models/photographer/photographer-rejected-shot.model';
import { PhotographerStyle } from '../models/photographer/photographer-style.model';
import { ReplaceShot } from '../models/photographer/replace-shot.model';
import { ImagePath, UploadShotsData } from '../models/photographer/upload-shots-data.models';
import { UploadedShot } from '../models/photographer/uploaded-shot.model';
import { StudiosHttpClientService } from '../shared/services/studios-http-client.service';
import { UtilitiesService } from '../shared/services/utilities.service';
import { SubstitutionShotData } from '../models/photographer/substitution-shot-data.type';


@Injectable()
export class PhotographerService {

  private baseUrl: string;
  private imgValidFileExtensions: string[] = ['jpg', 'jpeg', 'png'];

  constructor(
    private appService: AppService,
    private studiosHttpClient: StudiosHttpClientService,
    private utilitiesService: UtilitiesService
  ) {
    this.baseUrl = this.appService.utilsUrls.apiUrl;
  }

  public getCompanies(loaderId?: string): Observable<Company[]> {
    const url = this.baseUrl + '/Photographer/GetCompanies';
    return this.studiosHttpClient.get<Company[]>(url, {
      params: { currentUserId: this.appService.user.userId.toString(), photographerId: this.appService.photographerId.toString() }
    }, loaderId);
  }

  public sendCollaborationRequest(companyId: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Photographer/SendCollaborationRequest';
    return this.studiosHttpClient.post<number>(url, null, {
      params: {
        companyId: companyId.toString(),
        currentUserId: this.appService.user.userId.toString(),
        personId: this.appService.photographerId.toString()
      }
    }, loaderId);
  }

  public getPhotographerOrderDetails(orderId: number, loaderId?: string): Observable<OrderDetailsSchema<PhotographerOrderDetails>> {
    const url = this.baseUrl + '/Photographer/GetPhotographerOrderDetails';
    return this.studiosHttpClient.get<OrderDetailsSchema<PhotographerOrderDetails>>(url, {
      params: { photographerId: this.appService.photographerId.toString(), orderId: orderId.toString() }
    }, loaderId);
  }

  public getPhotographerUploadedShots(orderId: number, loaderId?: string): Observable<UploadedShot[]> {
    const url = this.baseUrl + '/Photographer/GetPhotographerUploadedShots';
    return this.studiosHttpClient.get<UploadedShot[]>(url, {
      params: { photographerId: this.appService.photographerId.toString(), orderId: orderId.toString() }
    }, loaderId);
  }

  public assignShot(orderImageId: number, shotId: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Photographer/AssignShot';
    const body: AssignShotData = { OrderImageId: orderImageId, ShotId: shotId };
    return this.studiosHttpClient.post<number>(url, body, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public subShot(orderImageId: number, shotId: number, loaderId?: string, invert?: number ): Observable<number> {
    const url = this.baseUrl + '/Photographer/SubstitutionShot';
    const body: SubstitutionShotData = { OrderImageId: orderImageId, ShotId: shotId, Invert: invert ?? 0 };
    return this.studiosHttpClient.post<number>(url, body, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getOrderImageId(shotId: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Photographer/GetOrderImageId';
    return this.studiosHttpClient.get<number>(url, {
      params: { shotId: shotId.toString() }
    }, loaderId);
  }

  public uploadShot(files: FileList, orderId: number, loaderId?: string): Observable<number> {
    const subject = new Subject<number>();
    const formData = new FormData();
    if (files && files.length) {
      let validFilesFlag = true;
      const filesNames = [];
      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        filesNames.push(file.name);
        const nameArray = file.name.split('.');
        const validFormat = this.imgValidFileExtensions.some(x => x === nameArray[nameArray.length - 1].toLowerCase());
        validFormat ? formData.append('uploadingFiles', file) : validFilesFlag = false;
      }
      if (validFilesFlag) {
        this.utilitiesService.uploadFile(formData, loaderId).subscribe(data => {
          if (data && data.length) {
            const body = new UploadShotsData({
              OrderId: orderId,
              ImagePaths: data.map((x, i) => new ImagePath(x.pathName, filesNames[i] ?? x.pathName))
            });
            const url = this.baseUrl + '/Photographer/UploadShots';
            this.studiosHttpClient.post<number>(url, body, {
              params: { currentUserId: this.appService.user.userId.toString() }
            }, loaderId).subscribe(data => subject.next(data));
          }
        });
      } else {
        setTimeout(() => { subject.next(undefined); });
      }
    }
    return subject.asObservable();
  }

  public rejectShot(files: FileList, orderId: number, loaderId?: string): Observable<number> {
    const subject = new Subject<number>();
    const formData = new FormData();
    if (files && files.length) {
      let validFilesFlag = true;
      const filesNames = [];
      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        filesNames.push(file.name);
        const nameArray = file.name.split('.');
        const validFormat = this.imgValidFileExtensions.some(x => x === nameArray[nameArray.length - 1].toLowerCase());
        validFormat ? formData.append('uploadingFiles', file) : validFilesFlag = false;
      }
      if (validFilesFlag) {
        this.utilitiesService.uploadFile(formData, loaderId).subscribe(data => {
          if (data && data.length) {
            const body = new UploadShotsData({
              OrderId: orderId,
              ImagePaths: data.map((x, i) => new ImagePath(x.pathName, filesNames[i] ?? x.pathName))
            });
            const url = this.baseUrl + '/Photographer/UploadShots';
            this.studiosHttpClient.post<number>(url, body, {
              params: { currentUserId: this.appService.user.userId.toString() }
            }, loaderId).subscribe(data => subject.next(data));
          }
        });
      } else {
        setTimeout(() => { subject.next(undefined); });
      }
    }
    return subject.asObservable();
  }

  // public getOrders(paginationPage: number, paginationSize: number, search: string, onGoing: boolean, filters: OrdersListFilters, loaderId?: string): Observable<OrdersListSchema<OrderListItem>> {
  //   const url = this.baseUrl + '/Photographer/GetPhotographerOrders';
  //   filters = filters || new OrdersListFilters({
  //     FromDueDate: null,
  //     NotSetDueDate: false,
  //     OrderStatusIds: [],
  //     PhotographerIds: [],
  //     ToDueDate: null
  //   })
  //   return this.studiosHttpClient.post<OrdersListSchema<OrderListItem>>(url, filters, {
  //     params: {
  //       photographerId: this.appService.photographerId.toString(),
  //       paginationPage: paginationPage.toString(),
  //       paginationSize: paginationSize.toString(),
  //       search: search || '',
  //       onGoing: onGoing ? 'true' : 'false'
  //     }
  //   }, loaderId);
  // }

  //   public getOrdersByPhotographerId(paginationPage: number, paginationSize: number, photographerId: string, onGoing: boolean, filters: OrdersListFilters, loaderId?: string): Observable<OrdersListSchema<OrderListItem>> {
  //   const url = this.baseUrl + '/Photographer/GetPhotographerOrders';
  //   filters = filters || new OrdersListFilters({
  //     FromDueDate: null,
  //     NotSetDueDate: false,
  //     OrderStatusIds: [],
  //     PhotographerIds: [],
  //     ToDueDate: null
  //   })
  //   return this.studiosHttpClient.post<OrdersListSchema<OrderListItem>>(url, filters, {
  //     params: {
  //       photographerId: photographerId,
  //       paginationPage: paginationPage.toString(),
  //       paginationSize: paginationSize.toString(),
  //       search: '',
  //       onGoing: onGoing ? 'true' : 'false'
  //     }
  //   }, loaderId);
  // }

  public getPhotographerRejectedShots(loaderId?: string): Observable<PhotographerRejectedShot[]> {
    const url = this.baseUrl + '/Photographer/GetPhotographerRejectedShots';
    return this.studiosHttpClient.get<PhotographerRejectedShot[]>(url, {
      params: { photographerId: this.appService.photographerId.toString() }
    }, loaderId);
  }

  public deleteShot(orderImageId: number, loaderId?: string): Observable<number> {
    const body = new DeleteShot(orderImageId, null);
    const url = this.baseUrl + '/Photographer/DeleteShot';
    return this.studiosHttpClient.post<number>(url, body, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getStylesToShoot(warehouseId: number, paginationPage: number, paginationSize: number, search?: string, loaderId?: string): Observable<PhotographerStyle[]> {
    const url = this.baseUrl + '/Photographer/GetStylesToShoot';
    return this.studiosHttpClient.get<PhotographerStyle[]>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        photographerId: this.appService.photographerId.toString(),
        warehouseId: warehouseId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public getPhotographerMaxItems(loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Photographer/GetPhotographerMaxItems';
    return this.studiosHttpClient.get<number>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        photographerId: this.appService.photographerId.toString(),
      }
    }, loaderId);
  }

  public replaceShot(files: FileList, shotId: number, loaderId?: string): Observable<number> {
    const subject = new Subject<number>();
    if (files && files[0]) {
      const formData = new FormData();
      const file = files[0];
      const nameArray = file.name.split('.');
      const validFormat = this.imgValidFileExtensions.some(x => x === nameArray[nameArray.length - 1].toLowerCase());
      if (validFormat) {
        formData.append('uploadingFiles', file);
        this.utilitiesService.uploadFile(formData, loaderId).subscribe(data => {
          if (data && data[0]) {
            const body = new ReplaceShot({
              ShotId: shotId,
              ImgPath: data[0].pathName,
              FileName: file.name
            });
            const url = this.baseUrl + '/Photographer/ReplaceShot';
            this.studiosHttpClient.post<number>(url, body, {
              params: { currentUserId: this.appService.user.userId.toString() }
            }, loaderId).subscribe(data => subject.next(data));
          }
        });
      } else {
        setTimeout(() => { subject.next(undefined); });
      }
    }
    return subject.asObservable();
  }
}
